import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { useCallback, useMemo } from "react";
import {
  resetThreadInteractionMeterFilter,
  setMeterFilter as setNonThreadedMeterFilter,
  setThreadInteractionMeterFilter,
} from "store/slices/search";
import { MeterFilterParams } from "types/MeterFilterParams";
import { defaultMeterFilter, getIsMeterFilterActive } from "../utils";

/**
 * Hook to get and update the consensus meter filter for a non-threaded search or a specific thread interaction
 *
 * @param threadInteractionId - Optional. The ID of the thread interaction to get the filter for.
 *                              If provided, the filter will be updated for the specific thread interaction.
 *                              Otherwise, the global meter filter state will be set for non-threaded searches.
 * @returns The current meter filter state, whether the filter is active, and a function to update the meter filter state.
 */
export const useConsensusMeterFilter = (threadInteractionId?: string) => {
  const dispatch = useAppDispatch();

  const meterFilter =
    useAppSelector((state) =>
      threadInteractionId
        ? state.search.threadInteractionMeterFilters[threadInteractionId]
        : state.search.meterFilter
    ) ?? defaultMeterFilter;

  const isMeterFilterActive = useMemo(() => {
    return getIsMeterFilterActive(meterFilter);
  }, [meterFilter]);

  const setMeterFilter = useCallback(
    (filter: MeterFilterParams) => {
      if (threadInteractionId) {
        dispatch(
          setThreadInteractionMeterFilter({
            threadInteractionId,
            meterFilter: filter,
          })
        );
      } else {
        dispatch(setNonThreadedMeterFilter(filter));
      }
    },
    [dispatch, threadInteractionId]
  );

  const resetMeterFilter = useCallback(() => {
    if (threadInteractionId) {
      dispatch(resetThreadInteractionMeterFilter(threadInteractionId));
    } else {
      dispatch(setNonThreadedMeterFilter(defaultMeterFilter));
    }
  }, [dispatch, threadInteractionId]);

  return { meterFilter, setMeterFilter, isMeterFilterActive, resetMeterFilter };
};
